<template>
  <div class="detail_container">
    <v-container class="module_schemes">
      <v-data-table
        :calculate-widths="true"
        :headers="headers"
        :items="$store.state.ModuleSchemesModule.listEntries"
        :loading="$store.state.ModuleSchemesModule.loadingList"
        :items-per-page="25"
        class="elevation-1 module_schemes__table"
        loading-text="Загрузка..."
        sort-by="id"
        sort-desc
        @click:row="editItem"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-toolbar-title class="module_schemes__header"
              >Схемы</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn-toggle
              :value="typeFilter"
              mandatory
              dense
              color="primary"
              class="mb-2"
              @change="changeType"
            >
              <v-btn value="base" :disabled="loading">Базовые</v-btn>
              <v-btn value="user" :disabled="loading">Пользовательские</v-btn>
            </v-btn-toggle>

            <v-dialog v-model="dialog" max-width="1200">
              <v-card>
                <v-card-title class="text-h6 module_schemes__modal_title">
                  <span class="module_schemes__modal_name"
                    >Схема «{{ editedItem.name }}»</span
                  >
                  <v-chip small class="ml-3">
                    {{ editedItem.type === "base" ? "базовая" : "пользовательская" }}
                  </v-chip>
                  <v-spacer></v-spacer>
                  <v-btn
                    small
                    text
                    color="primary"
                    :href="frontLink(editedItem.id)"
                    target="_blank"
                    rel="noopener"
                  >
                    <v-icon left small>mdi-open-in-new</v-icon>
                    Перейти на фронт схемы
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="7" class="order-2 order-md-1">
                      <!-- схема сразу в окне; пока окно закрыто, id нет — предпросмотр пуст -->
                      <ModuleSchemePreview
                        ref="preview"
                        :scheme-id="dialog ? editedItem.id : null"
                        :slot-groups="slotGroups"
                        :hovered-slot="hoveredSlot"
                        @hover-slot="hoveredSlot = $event"
                        @pick-slot="pickSlot"
                      />
                    </v-col>
                    <v-col cols="12" md="5" class="module_schemes__modal_item order-1 order-md-2">
                      <InputStyledSimple
                        :data="editedItem.name"
                        :is-loading="loading"
                        :is-disabled="loading"
                        placeholder="Название"
                        @update-input="editedItem.name = $event"
                      />
                      <InputStyledSimple
                        :data="editedItem.code"
                        :is-loading="loading"
                        :is-disabled="loading"
                        placeholder="code (уникальный)"
                        @update-input="editedItem.code = $event"
                      />
                      <!-- объект, для которого подбирается оборудование: из объектов владельца схемы; сохраняется сразу,
                           потому что слоты схемы закрепляются за этим объектом -->
                      <v-autocomplete
                        :value="editedItem.id_object || null"
                        :items="objectItems"
                        item-value="id"
                        :item-text="objectText"
                        label="Объект"
                        :hint="
                          editedItem.id_object
                            ? 'Оборудование схемы закрепляется за выбранным объектом'
                            : 'Выберите объект: без него оборудование подбирать нельзя'
                        "
                        :class="{ module_schemes__object_empty: !editedItem.id_object }"
                        persistent-hint
                        clearable
                        outlined
                        dense
                        :loading="objectsLoading || objectSaving"
                        :disabled="objectSaving"
                        no-data-text="У владельца схемы нет объектов"
                        @change="onObjectChange"
                      />
                      <!-- выбор номенклатуры по слотам оборудования схемы -->
                      <ModuleSchemeSlots
                        ref="slots"
                        :scheme-id="dialog ? editedItem.id : null"
                        :id-object="editedItem.id_object || null"
                        :object-name="selectedObjectName"
                        :hovered-slot="hoveredSlot"
                        :variant-saving="variantSaving"
                        @groups="slotGroups = $event"
                        @hover-slot="hoveredSlot = $event"
                        @set-variant="onSetVariant"
                      />
                    </v-col>
                  </v-row>
                  <!-- SEO под схемой, на всю ширину окна: сгруппировано и свёрнуто по умолчанию -->
                  <v-expansion-panels v-model="seoPanel" flat multiple class="mt-4">
                    <v-expansion-panel style="border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px">
                      <v-expansion-panel-header class="text-caption text--secondary">
                        <span>
                          SEO
                          <span class="ml-2 module_schemes__seo_summary">{{ seoSummary }}</span>
                        </span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div class="module_schemes__modal_item">
                          <InputStyledSimple
                            :data="editedItem.seo_title"
                            :is-loading="loading"
                            :is-disabled="loading"
                            placeholder="SEO title"
                            @update-input="editedItem.seo_title = $event"
                          />
                          <TextAreaStyled
                            :data="editedItem.seo_description"
                            :is-outlined="true"
                            :is-loading="loading"
                            :placeholder="'SEO description'"
                            :rows-count="'2'"
                            @update-input="editedItem.seo_description = $event"
                          />
                          <TextAreaStyled
                            :data="editedItem.seo_keywords"
                            :is-outlined="true"
                            :is-loading="loading"
                            :placeholder="'SEO keywords'"
                            :rows-count="'1'"
                            @update-input="editedItem.seo_keywords = $event"
                          />
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card-text>
                <v-card-actions>
                  <v-btn color="blue darken-1" text class="mr-auto" @click="dialog = false">
                    Отмена
                  </v-btn>
                  <v-btn
                    color="blue"
                    text
                    :loading="loading"
                    :disabled="!editedItem.name || !editedItem.code || loading"
                    @click="saveEntry"
                  >
                    Сохранить
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-dialog v-model="dialogDelete" max-width="500">
              <v-card>
                <v-card-title class="text-h6 d-block text-center"
                  >Удалить схему «{{ editedItem.name }}»?</v-card-title
                >
                <v-card-actions>
                  <v-btn color="blue darken-1" text class="mr-auto" @click="dialogDelete = false">
                    Отмена
                  </v-btn>
                  <v-btn
                    color="red darken-1"
                    text
                    :loading="loading"
                    @click="deleteEntryConfirmed"
                  >
                    Удалить
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>

        <template v-slot:item.preview="{ item }">
          <ModuleSchemeThumb :src="item._thumbnail_url || null" :version="item.updated_at || null" class="my-1" />
        </template>

        <template v-slot:item.actions="{ item }">
          <!-- ссылка, а не кнопка: открывается в новой вкладке; клик не должен открывать окно редактирования строки -->
          <a
            :href="frontLink(item.id)"
            target="_blank"
            rel="noopener"
            title="Перейти на фронт схемы"
            class="module_schemes__front_link mr-2"
            @click.stop
          >
            <v-icon small>mdi-open-in-new</v-icon>
          </a>
          <v-icon small class="mr-2" @click.stop="editItem(item)">
            mdi-pencil
          </v-icon>
          <v-icon small @click.stop="deleteItem(item)"> mdi-delete </v-icon>
        </template>

        <template v-slot:no-data>
          {{ typeFilter === "base" ? "Базовых схем пока нет" : "Пользовательских схем пока нет" }}
        </template>
      </v-data-table>
    </v-container>
  </div>
</template>

<script>
import InputStyledSimple from "@/components/common/InputStyledSimple";
import TextAreaStyled from "@/components/common/TextAreaStyled";
import ModuleSchemePreview from "@/components/module-schemes/ModuleSchemePreview";
import ModuleSchemeThumb from "@/components/module-schemes/ModuleSchemeThumb";
import ModuleSchemeSlots from "@/components/module-schemes/ModuleSchemeSlots";
import { frontSchemeUrl } from "@/helpers/frontUrl";

export default {
  name: "ModuleSchemes",
  components: { InputStyledSimple, TextAreaStyled, ModuleSchemePreview, ModuleSchemeSlots, ModuleSchemeThumb },
  data: () => ({
    headers: [
      { text: "ID", value: "id", width: "70px" },
      { text: "", value: "preview", sortable: false, width: "232px" },
      { text: "Название", value: "name" },
      { text: "Владелец (id)", value: "id_user", width: "160px" },
      { text: "", value: "actions", sortable: false, width: "110px" },
    ],
    dialog: false,
    dialogDelete: false,
    editedItem: {},
    // слоты оборудования схемы (из ModuleSchemeSlots) и слот под курсором — общие для
    // предпросмотра и списка: наведение на элемент схемы подсвечивает строку и наоборот
    slotGroups: [],
    hoveredSlot: null,
    // вариант сборки узла сохраняется в layout схемы, пока идёт — переключатели вариантов недоступны
    variantSaving: false,
    // раскрытые панели окна: SEO свёрнут по умолчанию (пустой список) и при каждом открытии схемы
    seoPanel: [],
    // объекты владельца схемы для выбора и признаки загрузки/сохранения
    objects: [],
    objectsLoading: false,
    objectSaving: false,
    // ответ загрузки объектов устаревает, если за время запроса открыли другую схему
    objectsRequestId: 0,
  }),
  computed: {
    // объекты для выбора; сохранённый объект, которого нет в списке владельца, показываем как «Объект №…»
    objectItems() {
      const id = this.editedItem.id_object;
      if (id && !this.objects.some((object) => object.id === id)) {
        return [{ id, name: `Объект № ${id}`, address: null }, ...this.objects];
      }
      return this.objects;
    },
    selectedObjectName() {
      const object = this.objectItems.find((item) => item.id === this.editedItem.id_object);
      return object ? object.name || `Объект № ${object.id}` : "";
    },
    // что внутри свёрнутого SEO — чтобы не раскрывать ради проверки: «заполнено 2 из 3» / «не заполнено»
    seoSummary() {
      const filled = ["seo_title", "seo_description", "seo_keywords"].filter(
        (key) => (this.editedItem[key] || "").trim() !== ""
      ).length;
      return filled ? `· заполнено ${filled} из 3` : "· не заполнено";
    },
    loading() {
      return this.$store.state.ModuleSchemesModule.loadingList;
    },
    typeFilter() {
      return this.$store.state.ModuleSchemesModule.typeFilter;
    },
  },
  watch: {
    // закрытие окна любым способом (кнопка, esc, клик вне) — убираем ?open из адреса
    dialog(value) {
      if (!value) this.syncOpenQuery(null);
    },
  },
  async mounted() {
    // ?type=base|user — список переключается на тип схемы из ссылки (например, из карточки узла)
    const type = this.$route.query.type;
    if ((type === "base" || type === "user") && type !== this.typeFilter) {
      await this.$store.dispatch("ModuleSchemesModule/changeTypeFilter", type);
    } else {
      await this.$store.dispatch("ModuleSchemesModule/getListEntries");
    }
    this.openFromQuery();
  },
  methods: {
    // ?open=<id схемы> — открыть окно схемы по ссылке, как у узлов в «Узлах схемы»
    openFromQuery() {
      const id = Number(this.$route.query.open);
      if (!id) return;
      const item = this.$store.state.ModuleSchemesModule.listEntries.find((entry) => entry.id === id);
      if (item) this.editItem(item);
    },
    syncOpenQuery(id) {
      const current = this.$route.query.open;
      const next = id ? String(id) : undefined;
      if (current === next || (!current && !next)) return;
      const query = { ...this.$route.query };
      if (next) query.open = next;
      else delete query.open;
      this.$router.replace({ query }).catch(() => {});
    },
    frontLink(id) {
      return frontSchemeUrl(id);
    },
    changeType(value) {
      if (value === this.typeFilter) return;
      this.$store.dispatch("ModuleSchemesModule/changeTypeFilter", value);
    },
    // клик по элементу на схеме — подбор номенклатуры, как клик по строке списка;
    // с Shift/Ctrl/Cmd — слот добавляется к выбору для массового назначения
    pickSlot(payload) {
      if (this.$refs.slots) this.$refs.slots.pickFromSvg(payload);
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.seoPanel = [];
      this.loadObjectsFor(item);
      this.hoveredSlot = null;
      this.dialog = true;
      this.syncOpenQuery(item.id);
    },
    objectText(object) {
      const name = object.name || `Объект № ${object.id}`;
      return object.address ? `${name} — ${object.address}` : name;
    },
    // «мои объекты» — объекты владельца схемы (у схемы без владельца списка нет)
    async loadObjectsFor(item) {
      const requestId = ++this.objectsRequestId;
      this.objects = [];
      this.objectsLoading = !!item.id_user;
      if (!item.id_user) return;
      const objects = await this.$store.dispatch("ModuleSchemesModule/loadObjects", item.id_user);
      // устаревший ответ отбрасываем: признаком загрузки управляет более новый запрос
      if (requestId !== this.objectsRequestId) return;
      this.objects = objects;
      this.objectsLoading = false;
    },
    // Объект меняется и сохраняется сразу; при ошибке (например, объект не владельца) возвращаем прежний
    async onObjectChange(value) {
      const next = value || null;
      if (next === (this.editedItem.id_object || null)) return;
      const previous = this.editedItem.id_object || null;
      this.$set(this.editedItem, "id_object", next);
      this.objectSaving = true;
      const ok = await this.$store.dispatch("ModuleSchemesModule/updateObject", {
        id: this.editedItem.id,
        idObject: next,
      });
      this.objectSaving = false;
      if (!ok) this.$set(this.editedItem, "id_object", previous);
    },
    // Сменили вариант сборки узла в списке оборудования: сохраняем в layout схемы (бэк не даёт сменить вариант,
    // пока в текущем есть купленное — сообщение уже показано) и перезагружаем и схему (другая картинка модуля),
    // и список (другие слоты). Перезагружаем и при ошибке: переключатель возвращается к сохранённому варианту.
    async onSetVariant({ uid, variant }) {
      const id = this.editedItem.id;
      this.variantSaving = true;
      await this.$store.dispatch("ModuleSchemesModule/setModuleVariant", { id, uid, variant });
      this.variantSaving = false;
      this.hoveredSlot = null;
      if (this.$refs.preview) this.$refs.preview.load(id);
      if (this.$refs.slots) this.$refs.slots.load(id);
    },
    deleteItem(item) {
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },
    async saveEntry() {
      const response = await this.$store.dispatch(
        "ModuleSchemesModule/updateEntry",
        this.editedItem
      );
      if (response && !response.isError) this.dialog = false;
    },
    async deleteEntryConfirmed() {
      await this.$store.dispatch(
        "ModuleSchemesModule/deleteEntry",
        this.editedItem.id
      );
      this.dialogDelete = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.module_schemes {
  &__header {
    font-size: 1em;
  }
  &__table {
    // подпись и иконка сортировки не должны переноситься на вторую строку
    ::v-deep thead th {
      white-space: nowrap;
    }
    ::v-deep tbody tr {
      cursor: pointer;
    }
  }
  &__modal_title {
    flex-wrap: nowrap;
  }
  &__front_link {
    color: inherit;
    text-decoration: none;
    // иконка рядом с карандашом и корзиной — та же высота строки и вертикаль
    display: inline-flex;
    vertical-align: middle;
  }
  // объект не выбран — подсказка поля оранжевая: от него зависит, можно ли подбирать оборудование
  &__object_empty ::v-deep .v-messages__message {
    color: #bf5b00;
  }
  &__seo_summary {
    font-weight: 400;
  }
  &__modal_name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__modal_item {
    // колонка тянется до низа предпросмотра схемы, блок оборудования забирает остаток высоты
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 22px;
    // у v-input в Vuetify flex: 1 1 auto — в колонке поля растянулись бы; растягивается только блок оборудования
    > *:not(.module_scheme_slots) {
      flex: 0 0 auto;
    }
    padding-top: 1.5em !important;
    padding-bottom: 1em !important;
  }
}
</style>
